import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Percent, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ROICalculator = () => {
  const [investment, setInvestment] = useState(1000);
  const [revenue, setRevenue] = useState(1500);
  const [roi, setRoi] = useState(0);

  useEffect(() => {
    calculateROI();
  }, [investment, revenue]);

  const calculateROI = () => {
    const profit = revenue - investment;
    const calculatedRoi = (profit / investment) * 100;
    setRoi(parseFloat(calculatedRoi.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/roi-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>ROI Calculator: Free Marketing Return on Investment Tool</title>
        <meta name="description" content="Calculate your marketing ROI instantly with our free calculator. Make data-driven decisions by measuring the return on your marketing investments accurately." />
        <meta name="keywords" content="ROI calculator, return on investment calculator, marketing ROI, investment calculator, profit calculator" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free ROI Calculator: Measure Your Marketing Return on Investment" />
        <meta property="og:description" content="Calculate your marketing ROI instantly. Make smarter investment decisions with our free return on investment calculator." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free ROI Calculator: Measure Your Marketing Return on Investment" />
        <meta property="twitter:description" content="Calculate your marketing ROI instantly. Make smarter investment decisions with our free return on investment calculator." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "ROI Calculator",
            "description": "Free calculator to measure return on investment for marketing campaigns and business decisions",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate ROI percentage",
              "Investment and revenue tracking",
              "Results interpretation",
              "ROI improvement tips"
            ]
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        ROI (Return on Investment) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Investment</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="1500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {roi}%
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Return on Investment (ROI)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600">
          Your Return on Investment (ROI) is {roi}%. This means that for every dollar invested, you're generating a return of ${(roi / 100 + 1).toFixed(2)}. 
          {roi > 0 ? " This is a positive ROI, indicating a profitable investment." : roi < 0 ? " This is a negative ROI, indicating a loss on your investment." : " This indicates that you've broken even on your investment."}
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Improve ROI</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize your marketing strategies to increase revenue</li>
          <li>Reduce costs without sacrificing quality</li>
          <li>Focus on high-performing products or services</li>
          <li>Improve customer retention to increase lifetime value</li>
          <li>Regularly analyze and adjust your investment strategies</li>
        </ul>
      </div>
    </div>
  );
};

export default ROICalculator;
